import config from "../config";
import * as url from "../helpers/url_helper";
import { getResourceStore } from "./crudFactory";
import { passwordIssue } from "../helpers/passwordRules";

/**
 * 密碼重設 mock（ticket 12，需求書 MSG-03-02）。
 *
 * 兩個入口、同一條路:
 *   管理者在使用者列表按「重設密碼」→ POST /api/auth/reset-request { userId }
 *   使用者自己在忘記密碼頁輸入 Email → POST /api/auth/reset-request { email }
 * 兩者都只回「已寄出」,**不透露這個 Email 有沒有註冊**(防帳號列舉);
 * 真的有註冊時才真的發一張 30 分鐘的一次性 token。
 *
 * 端點:
 *   POST /api/auth/reset-request  { email? , userId? } → { sent: true, expiresInMinutes, devToken? }
 *   GET  /api/auth/reset-token/:token                  → { valid, reason?, email? }
 *   POST /api/auth/reset-password { token, password }  → { ok } | 400 { message }
 *   POST /api/auth/change-password { userId, oldPassword, newPassword } → { ok } | 400 { message }
 *   POST /api/auth/login          { account, password }               → { user, token } | 400 { message }
 *
 * change-password 是登入後自己改密碼(設計稿 24:9203),與上面的忘記密碼流程分開:
 * 它要驗舊密碼,不發 token。
 *
 * devToken 只在 mock 回傳(真後端不會回),讓開發與測試走得完整條路。
 */

const TTL_MINUTES = 30;

const latency = () => config.MOCK_LATENCY;
const reply = (status, body) =>
  new Promise((resolve) => setTimeout(() => resolve([status, body]), latency()));

// token → { email, expiresAt, usedAt }
const tokens = new Map();
// userId → 目前密碼。種子使用者沒有密碼,所以這裡只記「系統知道的」:
// 由管理者新增的帳號會寫入初始密碼,之後每次變更都覆蓋。
// 查不到紀錄的(種子資料)一律放行,否則 demo 帳號全部登不進去。
const passwords = new Map();

/** 管理者新增使用者時設定的初始密碼 —— 明文不留在使用者資料列上 */
export function setInitialPassword(userId, password) {
  if (password) passwords.set(String(userId), password);
}

/** 這個帳號的密碼是不是系統設定的(沒紀錄=種子資料,不驗)。
 * 也給真後端契約的 GetToken mock 共用,兩個登入入口驗證規則一致。 */
export const credentialsValid = (userId, input) => passwordMatches(userId, input);

const passwordMatches = (userId, input) => {
  const stored = passwords.get(String(userId));
  if (stored === undefined) return String(input || "").trim().length > 0;
  return stored === input;
};
let seq = 1;

// 使用者的真實資料在 resource store 裡(seed 只是初始值);
// 讀 seed 的話,由畫面新增出來的帳號會登不進去也收不到重設信。
const userRows = () => {
  const store = getResourceStore("user");
  return store ? store.all() : [];
};

const idFromUrl = (u) => {
  const path = u.split("?")[0];
  return decodeURIComponent(path.substring(path.lastIndexOf("/") + 1));
};

// 密碼強度規則移至 helpers/passwordRules(單一來源);re-export 維持既有 import 路徑
export { passwordIssue };

// —— 真後端契約(DcCustomer/PasswordRecovery*)也用同一份 token 機制(ticket 09)——
/** 對這個 email 發一張一次性重設 token(30 分鐘) */
export const issueRecoveryToken = (email) => issueToken(email, Date.now());
/** token 目前狀態:{ valid, email? , reason? } */
export const recoveryTokenState = (token) => tokenState(token, Date.now());
/** 一次性:提交成功後作廢 */
export const consumeRecoveryToken = (token) => {
  const rec = tokens.get(token);
  if (rec) rec.usedAt = Date.now();
};

function issueToken(email, now) {
  const token = `rst-${seq++}`;
  tokens.set(token, { email, expiresAt: now + TTL_MINUTES * 60 * 1000, usedAt: null });
  return token;
}

function tokenState(token, now) {
  const rec = tokens.get(token);
  if (!rec) return { valid: false, reason: "not-found" };
  if (rec.usedAt) return { valid: false, reason: "used" };
  if (now > rec.expiresAt) return { valid: false, reason: "expired" };
  return { valid: true, email: rec.email };
}

export default function registerPasswordReset(mock) {
  const base = url.DCK.AUTH;

  // setupMock() 會重新註冊一次 = 重建 mock 世界。這兩份狀態是模組層級的,
  // 不在這裡清掉的話,測試之間會因為 id 重用而互相串味。
  tokens.clear();
  passwords.clear();

  mock.onPost(`${base}/reset-request`).reply((cfg) => {
    const p = cfg.data ? JSON.parse(cfg.data) : {};
    const rows = userRows();
    const target = p.userId
      ? rows.find((u) => String(u.id) === String(p.userId))
      : rows.find((u) => u.email && u.email.toLowerCase() === String(p.email || "").toLowerCase());

    // 查無此人也照樣回「已寄出」—— 回應不能洩漏帳號存不存在
    const body = { sent: true, expiresInMinutes: TTL_MINUTES };
    if (target) body.devToken = issueToken(target.email, Date.now());
    return reply(200, body);
  });

  mock.onGet(new RegExp(`^${base}/reset-token/[^/]+$`)).reply((cfg) =>
    reply(200, tokenState(idFromUrl(cfg.url), Date.now()))
  );

  mock.onPost(`${base}/reset-password`).reply((cfg) => {
    const p = cfg.data ? JSON.parse(cfg.data) : {};
    const state = tokenState(p.token, Date.now());
    if (!state.valid) {
      return reply(400, { message: "重設連結已失效,請重新申請。", reason: state.reason });
    }
    const issue = passwordIssue(p.password);
    if (issue) return reply(400, { message: issue });

    tokens.get(p.token).usedAt = Date.now(); // 一次性:用過就作廢
    return reply(200, { ok: true, email: state.email });
  });

  mock.onPost(`${base}/change-password`).reply((cfg) => {
    const p = cfg.data ? JSON.parse(cfg.data) : {};
    const key = String(p.userId || "");
    const oldPassword = String(p.oldPassword || "");

    if (!oldPassword) return reply(400, { message: "請輸入舊密碼。" });
    if (!passwordMatches(key, oldPassword)) {
      return reply(400, { message: "舊密碼不正確。" });
    }

    const issue = passwordIssue(p.newPassword);
    if (issue) return reply(400, { message: issue });
    if (p.newPassword === oldPassword) {
      return reply(400, { message: "新密碼不可與舊密碼相同。" });
    }

    passwords.set(key, p.newPassword);

    // 首登強制變更完成 → 拿掉標記(ticket 34)
    const store = getResourceStore("user");
    if (store) store.patch(key, { mustChangePassword: false });

    return reply(200, { ok: true });
  });

  // 登入。密碼錯誤刻意回 400 而不是 401 —— 401 被攔截器用來表示
  // 「session 失效」會觸發全站登出導回登入頁,在登入頁上回 401 只會自己打自己。
  mock.onPost(`${base}/login`).reply((cfg) => {
    const p = cfg.data ? JSON.parse(cfg.data) : {};
    const account = String(p.account || "").trim().toLowerCase();
    const hit = userRows().find(
      (u) =>
        (u.email || "").toLowerCase() === account ||
        (u.account || "").toLowerCase() === account
    );
    // 帳號不存在與密碼錯誤給同一句話,不透露這個帳號在不在
    const fail = () => reply(400, { message: "帳號或密碼錯誤，請重新輸入。" });
    if (!hit) return fail();
    if (!passwordMatches(hit.id, p.password)) return fail();

    return reply(200, { user: { ...hit }, token: `mock-token-${hit.id}` });
  });
}

/** 測試/重新註冊時清空發出去的 token 與改過的密碼 */
export const clearResetTokens = () => { tokens.clear(); passwords.clear(); };
